import { useEffect, useMemo, FC, ReactNode } from "react";
import { useController, useFormContext } from "react-hook-form";

import { SubmitGroup, useInputDisabled, useInputReadonly, ValidityGroup } from "capitalrx-components-2.0/lib/forms";
import { FieldProps as BaseFieldProps } from "capitalrx-components-2.0/lib/forms/Textbox/Field";
import { required as requiredRule, DisabledRequiredRule } from "capitalrx-components-2.0/lib/forms/validation";

import { Input } from "./Input";

/**
 * The `Field` component properties.
 */
export interface FieldProps extends BaseFieldProps {
  readonly name: string;
  /**
   * Whether to render the country code dropdown input
   */
  readonly useCountryCode?: boolean;

  /**
   * The Phonebox tooltip.
   */
  readonly tooltip?: ReactNode;
}

/**
 * A React Hook Form compatible Phonebox Field component.
 *
 * For more information, see the documentation for
 * the <a href="https://react-hook-form.com/api">React Hook Form API</a>.
 *
 * The phone-number-input library will always return
 * a phone number with the area code so you must handle that value accordingly
 * https://gitlab.com/catamphetamine/react-phone-number-input
 */
export const Field: FC<FieldProps> = (props) => {
  const { name, required = false, defaultValue = null, useCountryCode = false, validator, ...restProps } = props;

  const disabled = useInputDisabled(props);
  const readOnly = useInputReadonly(props);

  const rules = useMemo(
    () => ({
      ...(disabled || readOnly ? DisabledRequiredRule : requiredRule(required)),
      validate: disabled || readOnly ? undefined : validator,
    }),
    [disabled, readOnly, required, validator],
  );

  const { resetField } = useFormContext();

  useEffect(() => {
    if (disabled) {
      resetField(name);
    }
  }, [disabled]);

  const { field, fieldState, formState } = useController({
    name,
    defaultValue,
    rules,
  });

  const { ref, value, onChange, onBlur } = field;
  const { invalid, error } = fieldState;
  const { isSubmitting } = formState;

  return (
    <SubmitGroup submitting={isSubmitting}>
      <ValidityGroup invalid={invalid} error={error?.message}>
        <Input
          {...restProps}
          ref={ref}
          name={name}
          value={value}
          useCountryCode={useCountryCode}
          useReactHookFormVersion={true}
          required={required}
          defaultValue={defaultValue}
          onChange={onChange}
          onFocusExit={onBlur}
        />
      </ValidityGroup>
    </SubmitGroup>
  );
};
